import * as React from "react";
import { Info } from "lucide-react";
import { Pill, type PillTone } from "@/components/ui/pill";
import { cn } from "@/lib/utils";
import { MicroBar, VitalCell } from "./_vitals-cells";
import type { OeeBreakdownDetail } from "@/types/oee.types";
import type { OeeResponse } from "@opus-dt/shared-types";
import type { LinePeriodFilter } from "@/types/period-filter.types";

const CONFIDENCE_TONE: Record<string, PillTone> = {
  HIGH: "success",
  MEDIUM: "info",
  LOW: "warning",
  INSUFFICIENT: "neutral",
};
const CONFIDENCE_LABEL: Record<string, string> = {
  HIGH: "alta",
  MEDIUM: "média",
  LOW: "baixa",
  INSUFFICIENT: "insuf.",
};

export interface VitalsOeeHeroProps {
  oee: OeeResponse | undefined;
  period: LinePeriodFilter;
  onOpenMetric: (metric: keyof OeeBreakdownDetail) => void;
}

export function VitalsOeeHero({
  oee,
  period,
  onOpenMetric,
}: VitalsOeeHeroProps): React.ReactElement {
  const confidence = oee?.confidenceLevel ?? "INSUFFICIENT";
  const oeeNumColor = confidence === "LOW" ? "text-rd-warning-700" : "text-text-primary";

  return (
    <VitalCell
      label={
        <div className="flex items-center justify-between gap-2.5">
          <span className="inline-flex items-center gap-1">
            OEE · {period === "active" ? "Ativa" : period}
            <button
              type="button"
              onClick={() => onOpenMetric("oee")}
              aria-label="Explicar cálculo do OEE"
              className="rounded p-0.5 normal-case tracking-normal text-text-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]"
            >
              <Info className="h-3 w-3" />
            </button>
          </span>
          {oee ? (
            <button
              type="button"
              onClick={() => onOpenMetric("confidence")}
              aria-label="Explicar nível de confiança"
              className="rounded focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]"
            >
              <Pill tone={CONFIDENCE_TONE[confidence]} withDot>
                Confiança {CONFIDENCE_LABEL[confidence]}
              </Pill>
            </button>
          ) : null}
        </div>
      }
      className={cn(confidence === "LOW" && "bg-gradient-to-b from-[#fffdf8] to-rd-warning-50")}
    >
      <div className="flex items-baseline gap-1.5">
        <span
          className={cn(
            "font-mono text-[38px] font-medium leading-none tracking-[-0.03em]",
            oeeNumColor
          )}
        >
          {oee?.oee != null ? oee.oee.toFixed(1) : "—"}
        </span>
        <span className="font-mono text-[18px] text-text-secondary">%</span>
      </div>
      {}
      <div className="mt-2 flex gap-3.5">
        <MicroBar
          label="Disp."
          value={oee?.availability}
          color="#16a34a"
          onInfo={() => onOpenMetric("availability")}
          infoAriaLabel="Explicar cálculo de Disponibilidade"
        />
        <MicroBar
          label="Desemp."
          value={oee?.performance}
          color="#dc2626"
          onInfo={() => onOpenMetric("performance")}
          infoAriaLabel="Explicar cálculo de Desempenho"
        />
        <MicroBar
          label="Qual."
          value={oee?.quality}
          color="#f59e0b"
          onInfo={() => onOpenMetric("quality")}
          infoAriaLabel="Explicar cálculo de Qualidade"
        />
      </div>
    </VitalCell>
  );
}
